/*@flow*/
import React, { useEffect, useState } from 'react';
import AsyncDropdown from 'comps/AsyncDropdown';
import type { LocalsT } from 'lib/proforma/types';
import select from 'lib/proforma/templates/Select';
import * as loaders from 'queries/loaders';
import GetMemberResource from 'queries/GetMemberResource';
import useCachedQuery from 'lib/useCachedQuery';
import Present from 'lib/Present';
import getCompanyId from 'lib/getCompanyId';
import renameKeys from 'lib/renameKeys';
import hash from 'lib/hash';
import * as I from 'immutable';
import pick from 'ramda/src/pick';
import compose from 'ramda/src/compose';
import { useLocals } from 'lib/proforma/hooks';

function useResource(id, resourceName, companyId) {
  const variables = { companyId, resourceName, id };
  const skip = !id || !companyId;
  const opts = { variables, skip };
  const { data, ...rest } = useCachedQuery(GetMemberResource, opts);
  const record = I.getIn(data, ['company', 'resource'], {}) || {};
  return { ...rest, data: record };
}

function useTenant(id: ID) {
  const companyId = window.companyId;
  const resourceName = 'Tenant';
  return useResource(id, resourceName, companyId);
}

const getTenantFields = compose(
  renameKeys({ name: 'locationName' }),
  pick(['name', 'theGeom', 'address'])
);

export default select.clone({
  Select: (locals: LocalsT) => {
    getCompanyId();
    const { onChange, attrs, noOptionsMessage, value, namespace } = locals;
    let { placeholder } = attrs || {};
    const topLoc = useLocals([]);
    const locPath = [namespace, 'locationName'].filter(Boolean);
    const addressPath = [namespace, 'address'].filter(Boolean);
    const theGeomPath = [namespace, 'theGeom'].filter(Boolean);
    const locationNameLoc = useLocals(locPath);
    const addressLoc = useLocals(addressPath);
    const theGeomLoc = useLocals(theGeomPath);
    const { data, loading } = useTenant(value);
    const fieldsToChange = getTenantFields(data);
    let fieldValueMap = {
      locationName: locationNameLoc.value,
      address: addressLoc.value,
      theGeom: theGeomLoc.value,
    };
    if (namespace) {
      fieldValueMap = { [namespace]: fieldValueMap };
    }

    useEffect(() => {
      if (Present.is(fieldsToChange)) {
        let f1 = fieldsToChange;
        if (namespace) {
          f1 = { [namespace]: fieldsToChange };
        }

        if (hash(f1) === hash(fieldValueMap)) return;

        // Set the Tenant fields in their respective form fields.
        topLoc.onChange((v) => {
          if (v.merge) {
            const v2 = { ...fieldsToChange, tenantId: value };
            let v3 = I.Map(v2);
            if (namespace) {
              v3 = I.Map({ [namespace]: v2 });
            }
            return v.mergeDeep(v3);
          }
          return v;
        });
      }
    }, [fieldsToChange, topLoc]);

    const [options, setOptions] = useState([]);
    const theGeom = theGeomLoc.value;

    const loadOptions = () => {
      const resourceName = 'Tenant';
      let filters = {
        limit: 10,
      };
      const orderBy = { theGeom };
      if (theGeom) {
        filters = { ...filters, location: { theGeom, radius: 200 } };
      }

      const args = {
        resourceName,
        filters,
        orderBy,
      };
      return loaders.memberVenuesLoader(args).then((res: any) =>
        res.map((v: { name: string, id: string }) => ({
          label: v.name,
          value: v.id,
        }))
      );
    };

    useEffect(() => {
      window.companyId &&
        theGeom &&
        loadOptions().then((opts) => {
          setOptions(opts);
        });
    }, [theGeom]);

    const option = options.find((v) => String(v.value) === String(value));

    return (
      <AsyncDropdown
        isMulti={false}
        noOptionsMessage={noOptionsMessage}
        value={option}
        isLoading={loading}
        loadOptions={loadOptions}
        placeholder={loading ? 'Please wait...' : placeholder}
        isDisabled={loading}
        onChange={(v: any | any[]) => {
          if (Array.isArray(v)) {
            onChange(v[v.length - 1]);
          } else {
            onChange(v);
          }
        }}
      />
    );
  },
});
